import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UppyUploadWidget from '../../../../../components/widgets/UppyUploadWidget';

/** 화면 최초 렌더 시 «연결된 추가양식 (첨부파일)» 섹션 펼침 여부. false = 접힌 상태 */
const DEFAULT_ATTACHED_FORMS_SECTION_EXPANDED = false;

const DEFAULT_ATTACHED_FORM_ITEM_DEFS = [
  { id: 'doc1', titleKey: 'alli.create.attachedDoc1', required: false, allowedExtensions: ['pdf', 'hwp', 'hwpx', 'doc', 'docx'] },
  { id: 'doc2', titleKey: 'alli.create.attachedDoc2', required: false, allowedExtensions: ['pdf', 'hwp', 'hwpx'] },
  { id: 'doc3', titleKey: 'alli.create.attachedDoc3', required: false, allowedExtensions: ['pdf', 'hwp', 'hwpx', 'doc', 'docx', 'xls', 'xlsx'], maxFileCount: 1 },
];

function resolveAttachedFormItems(items, t) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const title = item.titleKey && t ? t(item.titleKey) : item.title;
    return { ...item, title };
  });
}

function normalizeAttachedFormItems(items, t) {
  if (!Array.isArray(items)) return [];
  return items.map((item) => {
    const id = item.id || `doc-${Math.random().toString(36).slice(2, 9)}`;
    const title = item.title || (t ? t('alli.common.attachmentDoc') : '첨부 문서');
    const required = Boolean(item.required);
    const exts = Array.isArray(item.allowedExtensions) ? item.allowedExtensions : ['pdf', 'hwp', 'hwpx', 'doc', 'docx'];
    const allowedExtensions = exts.map((e) => (e.startsWith('.') ? e.slice(1) : e).toLowerCase());
    const accept = allowedExtensions.map((e) => `.${e}`).join(',');
    const maxFileCount = typeof item.maxFileCount === 'number' && item.maxFileCount >= 1 ? item.maxFileCount : 1;
    return { id, documentType: title, required, accept, allowedExtensions, maxFileCount };
  });
}

export function getDefaultAttachedFormConfig(t) {
  const items = resolveAttachedFormItems(DEFAULT_ATTACHED_FORM_ITEM_DEFS, t);
  return normalizeAttachedFormItems(items, t);
}

function getAllowedExtensions(item) {
  if (Array.isArray(item.allowedExtensions) && item.allowedExtensions.length > 0) {
    return item.allowedExtensions.map((e) => (e.startsWith('.') ? e.slice(1) : e).toLowerCase());
  }
  const accept = item.accept;
  if (!accept || typeof accept !== 'string') return ['pdf', 'hwp', 'hwpx', 'doc', 'docx'];
  return accept
    .split(',')
    .map((s) => s.trim().replace(/^\./, '').toLowerCase())
    .filter(Boolean);
}

const AttachedFormsSection = ({ config = [], attachments = {}, errors = {}, onChange, onRemove }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(DEFAULT_ATTACHED_FORMS_SECTION_EXPANDED);
  const uppyRefs = useRef({});

  useEffect(() => {
    if (!Array.isArray(config)) return;

    config.forEach((item) => {
      const widget = uppyRefs.current[item.id];
      const uppy = widget?.getUppy?.();
      if (!uppy) return;

      const attached = attachments[item.id];
      const targetId = attached?.fileId ? `saved-${item.id}-${attached.fileId}` : null;
      const existingFiles = uppy.getFiles();

      if (attached?.fileId) {
        const alreadyExists = existingFiles.some((file) => file.id === targetId || file.meta?.fileId === attached.fileId);
        if (!alreadyExists) {
          try {
            uppy.addFile({
              id: targetId,
              name: attached.orgFileNm || attached.fileId,
              type: attached.fileType || 'application/octet-stream',
              data: new Blob([], { type: attached.fileType || 'application/octet-stream' }),
              meta: {
                fileId: attached.fileId,
                fileUid: attached.fileUid,
                name: attached.orgFileNm || attached.fileId,
                saved: attached.saved === 'N' ? 'N' : 'Y',
                url: `/api/glow/files/download?fileId=${encodeURIComponent(attached.fileId)}`,
              },
            });
            uppy.setFileState(targetId, {
              progress: {
                uploadComplete: true,
                uploadStarted: true,
                bytesTotal: 0,
                bytesUploaded: 0,
              },
            });
          } catch (e) {
            console.warn('[AttachedFormsSection] failed to inject existing file', e);
          }
        }
        return;
      }

      existingFiles.forEach((file) => {
        if (String(file.id || '').startsWith(`saved-${item.id}-`)) {
          uppy.removeFile(file.id);
        }
      });
    });
  }, [attachments, config]);

  if (!Array.isArray(config) || config.length === 0) return null;

  return (
    <div className="space-create-attached-form">
      <div className="card rounded-3 border">
        <div className="card-body">
          <section className="space-create-attached-section">
            <div className="space-create-section-title-block">
              <div className="space-create-attached-title-row d-flex align-items-center justify-content-between gap-3 flex-wrap">
                <div className="space-create-section-title-inner min-w-0 flex-grow-1">
                  <span className="space-create-section-title-bar" aria-hidden="true" />
                  <h3 className="space-create-section-title mb-0">{t('alli.create.attachedSectionTitle')}</h3>
                </div>
                <button
                  type="button"
                  className="space-create-attached-toggle"
                  role="switch"
                  aria-checked={expanded}
                  aria-expanded={expanded}
                  aria-controls="space-create-attached-body"
                  onClick={() => setExpanded((v) => !v)}
                >
                  <span className="visually-hidden">
                    {expanded ? t('alli.create.attachedCollapse') : t('alli.create.attachedExpand')}
                  </span>
                </button>
              </div>
              <div className="space-create-section-title-line" />
            </div>

            {expanded && (
              <div id="space-create-attached-body">
            <p className="space-create-attached-desc">{t('alli.create.attachedDesc')}</p>
            <div className="row g-3">
              {config.map((item) => {
                const { id, documentType, required = false, maxFileCount: itemMaxFiles = 1 } = item;
                const err = errors[id];
                const allowedExtensions = getAllowedExtensions(item);
                const acceptDisplay = allowedExtensions.length ? allowedExtensions.join(', ').toUpperCase() : t('alli.create.attachedAllowedFormats');

                return (
                  <div key={id} className="col-12">
                    <div className={`space-create-attached-block ${err ? 'is-invalid-block' : ''}`}>
                      <div className="space-create-attached-block-header">
                        <span className="space-create-attached-label">
                          {documentType} {required && <span className="text-danger">*</span>}
                        </span>
                        <span className="space-create-attached-hint">{t('alli.create.attachedAllowed', { formats: acceptDisplay })}</span>
                      </div>
                      <div className="space-create-attached-uppy-wrap">
                        <UppyUploadWidget
                          ref={(el) => {
                            if (el) uppyRefs.current[id] = el;
                          }}
                          id={`attached-form-uppy-${id}`}
                          height={200}
                          maxFileCount={itemMaxFiles}
                          allowedExtensions={allowedExtensions}
                          onUploadSuccess={(file) => {
                            const meta = {
                              docId: id,
                              documentType,
                              fileId: file.meta?.fileId,
                              fileUid: file.meta?.fileUid,
                              orgFileNm: file.meta?.name || file.name,
                              fileType: file.meta?.type || file.type || '',
                              extension: file.extension || '',
                              saved: 'N',
                            };
                            onChange?.(id, meta);
                          }}
                          onFileRemoved={(file, reason) => {
                            if (reason === 'removed-by-user') onRemove?.(id);
                          }}
                          className="space-create-attached-uppy"
                        />
                      </div>
                      {err && <div className="invalid-feedback d-block mt-2">{err}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
              </div>
            )}
          </section>
        </div>
      </div>

      <style>{`
        .space-create-attached-form { margin-top: 24px; margin-bottom: 24px; }
        .space-create-attached-section .space-create-section-title-block { margin-bottom: 20px; }
        .space-create-attached-title-row { margin-bottom: 0; }
        .space-create-attached-toggle {
          position: relative;
          flex-shrink: 0;
          width: 44px;
          height: 26px;
          padding: 0;
          border: none;
          border-radius: 999px;
          background: #cbd5e1;
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        .space-create-attached-toggle:hover {
          background: #94a3b8;
        }
        .space-create-attached-toggle[aria-checked="true"] {
          background: #0d6efd;
        }
        .space-create-attached-toggle[aria-checked="true"]:hover {
          background: #0b5ed7;
        }
        .space-create-attached-toggle:focus-visible {
          outline: 2px solid #0d6efd;
          outline-offset: 2px;
        }
        .space-create-attached-toggle::after {
          content: "";
          position: absolute;
          top: 3px;
          left: 3px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #fff;
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
          transition: transform 0.2s ease;
        }
        .space-create-attached-toggle[aria-checked="true"]::after {
          transform: translateX(18px);
        }
        .space-create-attached-section .space-create-section-title-inner {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .space-create-attached-section .space-create-section-title-bar {
          width: 4px;
          height: 1.25em;
          background: var(--theme-color-primary);
          border-radius: 2px;
          flex-shrink: 0;
        }
        .space-create-attached-section .space-create-section-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
          color: #374151;
        }
        .space-create-attached-section .space-create-section-title-line {
          height: 1px;
          background: #e5e7eb;
          margin-top: 12px;
        }
        .space-create-attached-desc { font-size: 13px; color: #6b7280; margin-bottom: 20px; }
        .space-create-attached-block {
          height: 100%;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .space-create-attached-block:hover { border-color: #cbd5e1; }
        .space-create-attached-block.is-invalid-block { border-color: #dc3545; }
        .space-create-attached-block-header {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          justify-content: space-between;
          gap: 8px;
          margin-bottom: 12px;
        }
        .space-create-attached-label {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #334155;
        }
        .space-create-attached-hint {
          font-size: 12px;
          color: #64748b;
        }
        .space-create-attached-uppy-wrap {
          min-height: 200px;
          background: #fff;
          border-radius: 8px;
          border: 1px dashed #cbd5e1;
          overflow: hidden;
        }
        .space-create-attached-block .space-create-attached-uppy .uppy-upload-widget,
        .space-create-attached-block .space-create-attached-uppy [data-uppy-dashboard] {
          border: none;
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
};

export default AttachedFormsSection;
